/**
 * How often a device has transmitted (tri-server#1591): the successful uplinks per period from
 * `POST /customers/:customer/assets/transmission-counts`, for the operator's battery-drain
 * calculation. One request per asset; the result is kept until the asset changes.
 */

import { useCallback, useEffect, useState } from "react";

import { authenticatedFetch } from "../../JWTAuthService";
import { configuration } from "../../Configuration";

export interface TransmissionCounts {
  last7Days: number;
  last30Days: number;
  last90Days: number;
  /** Since the current activation. */
  sinceActivation: number;
}

export interface AssetTransmissionCounts {
  key: string;
  id: string;
  metric: string;
  counts: TransmissionCounts;
  /** ISO timestamps of the first and the latest counted uplink. */
  firstAt: string | null;
  lastAt: string | null;
}

export async function fetchTransmissionCounts(
  customer: string,
  assetKey: string,
): Promise<AssetTransmissionCounts | null> {
  const response = await authenticatedFetch(
    `${configuration.serverUrl}/customers/${customer}/assets/transmission-counts`,
    {
      method: "POST",
      body: JSON.stringify({ assets: [assetKey] }),
      headers: { "Content-Type": "application/json" },
    },
  );
  if (!response.ok) throw new Error(`transmission-counts ${response.status}`);

  const result = await response.json();

  return Array.isArray(result?.assets) ? (result.assets[0] ?? null) : null;
}

export interface TransmissionCountsState {
  data: AssetTransmissionCounts | null;
  loading: boolean;
  failed: boolean;
  retry: () => void;
}

export function useTransmissionCounts(
  customer: string | undefined,
  assetKey: string | undefined,
): TransmissionCountsState {
  const [data, setData] = useState<AssetTransmissionCounts | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setData(null);
    setFailed(false);
    if (!customer || !assetKey) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchTransmissionCounts(customer, assetKey)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [customer, assetKey, attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  return { data, loading, failed, retry };
}
